import React from 'react';
import {View} from 'react-native';
import {isEmpty} from 'lodash';

import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import {CoachInfoCard} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {getDateUtilDefaultInstance} from '/util/date.util';

import {styles} from './AllCoachesReportContent.style';

interface AllCoachesReportContentProps {
  tt: (key: string, fallback: string) => string;
  recordData: {
    activatedOn?: string;
    qualifyingPoints?: number;
    orderingEntitiesCount?: number;
    businessCoach?: {
      firstName?: string;
      lastName?: string;
    };
    highestAchievedRank?: {
      description?: string;
    };
  };
  renderTitle: () => string;
}

const AllCoachesReportContent: React.FC<AllCoachesReportContentProps> = ({
  tt,
  recordData = {},
  renderTitle = () => '',
}) => {
  const {
    businessCoach: {firstName = '', lastName = ''} = {},
    highestAchievedRank: {description: highestAchievedRankName = ''} = {},
    orderingEntitiesCount = 0,
    qualifyingPoints = 0,
    activatedOn = '',
  } = recordData;

  return (
    <View
      style={styles.base}
      accessible={true}
      accessibilityLabel="Coach information">
      <View style={styles.header}>
        <Gutter>
          <Text
            variant="heading"
            accessible={true}
            accessibilityRole="header"
            accessibilityLabel={renderTitle()}>
            {renderTitle()}
          </Text>
        </Gutter>
      </View>

      <Gutter>
        <View
          accessible={true}
          accessibilityLabel={`Activation Date ${getDateUtilDefaultInstance().formatDate(
            activatedOn,
            'MMMM d, yyyy',
          )}`}>
          <ListItem
            style={{base: styles.listItem}}
            readonly
            label={
              <Text accessible={false}>
                {tt('activationDate', 'Activation Date')}
              </Text>
            }
            suffix={
              <Text variant="button" accessible={false}>
                {getDateUtilDefaultInstance().formatDate(
                  activatedOn,
                  'MMMM d, yyyy',
                )}
              </Text>
            }
            testID={CoachInfoCard.activationDateContent}
          />
        </View>
      </Gutter>

      <Gutter>
        <View
          accessible={true}
          accessibilityLabel={`Business Coach ${firstName} ${lastName}`}>
          <ListItem
            style={{base: styles.listItem}}
            readonly
            label={
              <Text accessible={false}>
                {tt('businessCoach', 'Business Coach')}
              </Text>
            }
            suffix={
              <Text variant="button" accessible={false}>
                {`${firstName} ${lastName}`}
              </Text>
            }
            testID={CoachInfoCard.businessCoachContent}
          />
        </View>
      </Gutter>

      <Gutter>
        <View
          accessible={true}
          accessibilityLabel={`Highest Rank Achieved ${
            isEmpty(highestAchievedRankName)
              ? tt('notAvailable', 'N/A')
              : highestAchievedRankName
          }`}>
          <ListItem
            style={{base: styles.listItem}}
            readonly
            label={
              <Text accessible={false}>
                {tt('highestRankAchieved', 'Highest Rank Achieved')}
              </Text>
            }
            suffix={
              <Text variant="button" accessible={false}>
                {isEmpty(highestAchievedRankName)
                  ? tt('notAvailable', 'N/A')
                  : highestAchievedRankName}
              </Text>
            }
            testID={CoachInfoCard.harContent}
          />
        </View>
      </Gutter>

      <Gutter>
        <View
          accessible={true}
          accessibilityLabel={`Ordering Entities ${orderingEntitiesCount}`}>
          <ListItem
            style={{base: styles.listItem}}
            readonly
            label={
              <Text accessible={false}>
                {tt('orderingEntities', 'Ordering Entities')}
              </Text>
            }
            suffix={
              <Text variant="button" accessible={false}>
                {orderingEntitiesCount}
              </Text>
            }
            testID={CoachInfoCard.oeContent}
          />
        </View>
      </Gutter>

      <Gutter>
        <View
          accessible={true}
          accessibilityLabel={`Qualifying Points ${qualifyingPoints}`}>
          <ListItem
            style={{base: styles.listItem}}
            readonly
            label={
              <Text accessible={false}>
                {tt('qualifyingPoints', 'Qualifying Points')}
              </Text>
            }
            suffix={
              <Text variant="button" accessible={false}>
                {qualifyingPoints}
              </Text>
            }
            testID={CoachInfoCard.qualifyingPointsContent}
          />
        </View>
      </Gutter>
    </View>
  );
};

export default withTranslation('reportCardContent.allCoaches')(
  AllCoachesReportContent,
);
